import { useTranslation } from 'react-i18next';
import type { Party } from '@prody/shared';
import {
  Button, CHANNEL_ICONS, Code, CrateHeader, FactList, IconClose, IconLink,
  Section, Stamp, vars,
} from '~/design-system';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import './contacts.css';

/* ------------------------------------------------------------------ *
 * A contact, seen whole.
 *
 * The channel strip is the first thing in the panel and it is a row
 * of *buttons*, not a list of addresses: the brief asks for one place
 * to reach a person on every messenger, and the useful form of that
 * is a control that opens the thread, not a phone number you have to
 * copy.
 * ------------------------------------------------------------------ */

export function ContactManifest({
  party,
  onClose,
  embedded = false,
}: {
  party: Party;
  onClose: () => void;
  embedded?: boolean;
}) {
  const { t } = useTranslation(['contacts', 'common', 'messaging']);
  const { format } = useBusiness();

  const overLimit =
    party.paymentTerms.creditLimitMinor !== null &&
    party.paymentTerms.creditLimitMinor < 0;

  return (
    <div className="pd-contact">
      {!embedded ? (
        <CrateHeader
          handlingClass={party.roles.includes('supplier') ? 'chilled' : 'neutral'}
          code={party.code}
          title={party.name}
          subtitle={party.legalName ?? undefined}
          stamp={
            <span className="row" style={vars({ '--gap': 'var(--s-2)' })}>
              {party.roles.map((r) => (
                <Stamp key={r} tone={r === 'supplier' ? 'info' : r === 'ambassador' ? 'accent' : 'neutral'} size="sm">
                  {t(`roles.${r}`)}
                </Stamp>
              ))}
            </span>
          }
          facts={[
            { label: t('common:labels.sku'), value: <Code>{party.taxId ?? '—'}</Code> },
            {
              label: t('terms'),
              value:
                party.paymentTerms.netDays === 0
                  ? t('onDelivery')
                  : t('netDays', { days: party.paymentTerms.netDays }),
            },
            {
              label: t('creditLimit'),
              value:
                party.paymentTerms.creditLimitMinor === null
                  ? '—'
                  : format.money({
                      minor: party.paymentTerms.creditLimitMinor,
                      currency: format.currency,
                    }),
            },
          ]}
          actions={
            <Button variant="quiet" size="sm" iconOnly aria-label={t('common:actions.close')} icon={<IconClose />} onClick={onClose} />
          }
        />
      ) : null}

      <div className="pd-contact__body">
        {overLimit ? (
          <p className="pd-contact__alert">
            <Stamp tone="alert" weight="solid">{t('overLimit')}</Stamp>
          </p>
        ) : null}

        {/* ---- Reach them ---- */}
        <Section title={t('messaging:allChannels')}>
          <div className="pd-contact__channels">
            {party.channels.map((c) => {
              const Icon = CHANNEL_ICONS[c.kind];
              return (
                <button
                  key={`${c.kind}-${c.address}`}
                  type="button"
                  className="pd-contact__channel-btn"
                  data-muted={c.optedOut || undefined}
                  disabled={c.optedOut}
                  title={c.optedOut ? 'Opted out' : undefined}
                >
                  <Icon />
                  <span className="pd-contact__channel-meta">
                    <span className="stencil">{t(`common:channel.${c.kind}`)}</span>
                    <span className="num truncate">{c.address}</span>
                  </span>
                  {c.primary ? <Stamp tone="accent" size="sm">1</Stamp> : null}
                </button>
              );
            })}
          </div>
        </Section>

        {/* ---- People ---- */}
        {party.people.length > 0 ? (
          <Section title={t('people')}>
            <ul className="pd-contact__people">
              {party.people.map((person) => (
                <li key={person.id}>
                  <div className="spread">
                    <span className="pd-contact__person-name">{person.name}</span>
                    {person.isDecisionMaker ? (
                      <Stamp tone="accent" size="sm">{t('roles.customer')}</Stamp>
                    ) : null}
                  </div>
                  {person.role ? <p className="t-small dim">{person.role}</p> : null}
                  {person.notes ? <p className="pd-contact__person-note">{person.notes}</p> : null}
                </li>
              ))}
            </ul>
          </Section>
        ) : null}

        {/* ---- Delivery ---- */}
        {party.addresses.length > 0 ? (
          <Section title={t('addresses')}>
            <ul className="pd-contact__addresses">
              {party.addresses.map((address) => {
                const prefs = party.deliveryPreferences.filter((p) => p.addressId === address.id);
                return (
                  <li key={address.id}>
                    <div className="spread">
                      <span className="stencil">{address.label}</span>
                      {prefs.some((p) => p.strict) ? (
                        <Stamp tone="warn" size="sm">{t('deliveryWindows')}</Stamp>
                      ) : null}
                    </div>
                    <p>{address.line1}</p>
                    <p className="dim">
                      {address.postcode} {address.city}
                    </p>
                    {prefs.map((p, i) => (
                      <p key={i} className="pd-contact__window">
                        <span className="num">{format.list(p.weekdays)}</span>
                        <span className="num">{format.timeWindow(p.window)}</span>
                      </p>
                    ))}
                    {address.accessNotes ? (
                      <p className="pd-contact__access">{address.accessNotes}</p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </Section>
        ) : null}

        {/* ---- Supplier side ---- */}
        {party.supplier ? (
          <Section title={t('roles.supplier')}>
            <FactList
              columns={2}
              items={[
                { label: t('standCode'), value: party.supplier.standCode ?? '—' },
                {
                  label: t('runDays'),
                  value: party.supplier.runDays.length
                    ? format.list(party.supplier.runDays)
                    : '—',
                },
                { label: t('common:labels.time'), value: party.supplier.orderCutoff ?? '—' },
                {
                  label: t('common:labels.date'),
                  value: `${party.supplier.leadTimeDays} d`,
                },
              ]}
            />
          </Section>
        ) : null}

        {/* ---- Ambassador side ---- */}
        {party.ambassador ? (
          <Section title={t('roles.ambassador')}>
            <FactList
              columns={2}
              items={[
                {
                  label: t('commission'),
                  value: format.percentFromBps(party.ambassador.commissionBps),
                },
                {
                  label: t('common:labels.date'),
                  value: party.ambassador.durationMonths
                    ? `${party.ambassador.durationMonths} mo`
                    : '∞',
                },
              ]}
            />
          </Section>
        ) : null}

        {party.referredByPartyId ? (
          <Section title={t('referredBy')}>
            <Button variant="quiet" size="sm" icon={<IconLink />}>
              {party.referredByPartyId}
            </Button>
          </Section>
        ) : null}

        {party.notes ? (
          <Section title={t('common:labels.notes')}>
            <p className="pd-contact__notes">{party.notes}</p>
          </Section>
        ) : null}
      </div>
    </div>
  );
}
